import React, { useState, useEffect } from 'react'
import ReactModal from 'react-modal'
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Button
} from '@mui/material'
import { assessmentTemplatesApi } from '../../api/assessments'
import { AssessmentCard } from '../../components/assessments/AssessmentCard'
import { CreateAssessmentModal } from '../../components/assessments/CreateAssessmentModal'
import type { AssessmentTemplate, QuestionResponse } from '../../types/assessments'
import { Plus } from 'lucide-react'
import axios from 'axios'

export const TemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<any>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<AssessmentTemplate | null>(null)
  const [previewQuestions, setPreviewQuestions] = useState<QuestionResponse[]>([])
  const [loadingQuestions, setLoadingQuestions] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [templateForModal, setTemplateForModal] = useState<AssessmentTemplate | null>(null)

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true)
        const token = localStorage.getItem('auth-token')
        const response = await axios.get(
          `${process.env.REACT_APP_RESUME_PARSER_URL}/api/assessment-templates`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )
        console.log(response.data)
        setTemplates(response.data)
        setError(null)
      } catch (err: any) {
        setError(err.message || 'Failed to load templates')
      } finally {
        setLoading(false)
      }
    }

    fetchTemplates()
  }, [])

  const handleTemplateClick = async (template: any) => {
    setSelectedTemplate(template)
    setLoadingQuestions(true)
    try {
      // Use questions from template if available, otherwise try to fetch from API
      if (
        template.questions &&
        Array.isArray(template.questions) &&
        template.questions.length > 0
      ) {
        // Convert question strings to preview format
        const previewQuestions = template.questions.map((questionText: string, index: number) => ({
          id: index + 1,
          uuid: `preview-${index}`,
          assessmentId: template.uuid || '',
          order: index + 1,
          question: questionText,
          type: 'SINGLE' as const,
          options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
          status: 'ACTIVE' as const
        }))
        setPreviewQuestions(previewQuestions)
      } else {
        // Fallback: try to fetch from API if questions array is not available
        const questions = await assessmentTemplatesApi.getTemplateQuestions(template.uuid)
        setPreviewQuestions(questions)
      }
    } catch (err: any) {
      console.error('Failed to load template questions:', err)
      setPreviewQuestions([])
    } finally {
      setLoadingQuestions(false)
    }
  }

  const handleBlankTemplateClick = () => {
    setSelectedTemplate(null)
    setPreviewQuestions([])
  }

  const handleUseTemplate = (template: any) => {
    setTemplateForModal(template)
    setModalOpen(true)
  }

  const handleBlankTemplateUse = () => {
    setTemplateForModal(null)
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setTemplateForModal(null)
  }

  if (loading) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Alert severity='error'>{error}</Alert>
      </Container>
    )
  }

  return (
    <>
      <section className='row p-4'>
        {/* Left Side - Templates Grid */}
        <div className='col-8 d-flex flex-column'>
          <div className='card' style={{ border: 'none', boxShadow: 'none' }}>
            {/* Header */}
            <div
              className='card-header'
              style={{ borderBottom: '1px solid #e0e0e0', padding: '24px' }}
            >
              <h3 className='card-title mb-0 d-flex flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1 d-block'>
                  Select Assessment Templates
                </span>
                <span className='text-muted mt-1 fw-bold fs-7 d-block'>
                  Templates help you start building your own assessments
                </span>
              </h3>
            </div>

            {/* Body - Templates Grid */}
            <div className='card-body' style={{ padding: '24px' }}>
              {templates.length === 0 ? (
                <Box textAlign='center' py={8}>
                  <Typography variant='h6' color='text.secondary'>
                    No templates available
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(3, 1fr)'
                    },
                    gap: 3
                  }}
                >
                  {/* Blank Template Card */}
                  <Box
                    onClick={handleBlankTemplateClick}
                    sx={{
                      minHeight: '420px',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      border:
                        selectedTemplate === null ? '2px solid #3498db' : '2px dashed #d0d0d0',
                      backgroundColor: '#f8f9fa',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                        borderColor: '#3498db'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        p: 3,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 2,
                          backgroundColor: '#e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        <Plus size={32} color='#666' />
                      </Box>
                      <Typography
                        variant='h5'
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          textAlign: 'center',
                          color: '#212121',
                          fontSize: '1.25rem'
                        }}
                      >
                        Blank Template
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{
                          mb: 2,
                          textAlign: 'center',
                          color: 'text.secondary',
                          fontSize: '0.875rem'
                        }}
                      >
                        Start from scratch
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{
                          mb: 3,
                          textAlign: 'center',
                          color: 'text.secondary',
                          fontSize: '0.813rem',
                          flexGrow: 1,
                          lineHeight: 1.5
                        }}
                      >
                        Create a custom assessment template from the ground up
                      </Typography>
                      <Button
                        variant='contained'
                        fullWidth
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation()
                          handleBlankTemplateUse()
                        }}
                        sx={{
                          background: 'linear-gradient(180deg, #3498db 0%, #2980b9 100%)',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.813rem',
                          textTransform: 'uppercase',
                          py: 1.5,
                          borderRadius: 2,
                          boxShadow: '0 2px 4px rgba(52, 152, 219, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(180deg, #2980b9 0%, #3498db 100%)',
                            boxShadow: '0 4px 8px rgba(52, 152, 219, 0.4)'
                          }
                        }}
                      >
                        USE TEMPLATE
                      </Button>
                    </Box>
                  </Box>

                  {/* Template Cards */}
                  {templates?.data?.map((template: any) => (
                    <Box key={template.id} sx={{ minHeight: '420px', height: '100%' }}>
                      <AssessmentCard
                        template={template}
                        onPreview={() => handleTemplateClick(template)}
                        onUseTemplate={() => handleUseTemplate(template)}
                        isSelected={selectedTemplate?.id === template.id}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </div>
          </div>
        </div>

        {/* Right Side - Preview Panel */}
        <div className='col-4'>
          <div
            className='card'
            style={{
              height: '100%',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            <div
              className='card-header'
              style={{ borderBottom: '1px solid #e0e0e0', padding: '20px' }}
            >
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 700,
                  fontSize: '1.125rem',
                  color: '#212121',
                  mb: 0
                }}
              >
                {selectedTemplate ? `${selectedTemplate.title} - Preview` : 'Template Preview'}
              </Typography>
              {selectedTemplate && previewQuestions.length > 0 && (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mt: 0.5, fontSize: '0.875rem' }}
                >
                  {previewQuestions.length} Question{previewQuestions.length !== 1 ? 's' : ''}
                </Typography>
              )}
            </div>

            <div
              className='card-body scroll-y'
              style={{
                padding: '20px',
                maxHeight: 'calc(100vh - 250px)',
                overflowY: 'auto'
              }}
            >
              {!selectedTemplate ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.875rem' }}>
                    Select a template to preview questions and options
                  </Typography>
                </Box>
              ) : loadingQuestions ? (
                <Box display='flex' justifyContent='center' alignItems='center' py={8}>
                  <CircularProgress size={40} />
                </Box>
              ) : previewQuestions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.875rem' }}>
                    No questions available for preview
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {previewQuestions.map((question, index) => (
                    <Paper
                      key={question.id || question.uuid}
                      sx={{
                        p: 2.5,
                        mb: 2.5,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        backgroundColor: '#fff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1.5,
                          flexWrap: 'wrap'
                        }}
                      >
                        <Typography
                          variant='body2'
                          sx={{
                            fontWeight: 700,
                            color: selectedTemplate.color,
                            fontSize: '0.875rem'
                          }}
                        >
                          Q{index + 1}
                        </Typography>
                        <span
                          className='badge'
                          style={{
                            backgroundColor: `${selectedTemplate.color}15`,
                            color: selectedTemplate.color,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: '4px',
                            border: `1px solid ${selectedTemplate.color}30`
                          }}
                        >
                          {question.type}
                        </span>
                      </Box>

                      <Typography
                        variant='body1'
                        sx={{
                          fontWeight: 500,
                          mb: question.options && question.options.length > 0 ? 2 : 1.5,
                          fontSize: '0.938rem',
                          lineHeight: 1.5,
                          color: '#212121'
                        }}
                      >
                        {question.question}
                      </Typography>

                      {question.options && question.options.length > 0 && (
                        <Box sx={{ mt: 1.5, mb: 1.5 }}>
                          {question.options.map((option, optIndex) => (
                            <Box
                              key={optIndex}
                              sx={{
                                p: 1.25,
                                mb: 0.75,
                                borderRadius: 1,
                                backgroundColor: '#fafafa',
                                border: '1px solid #e8e8e8'
                              }}
                            >
                              <Typography
                                variant='body2'
                                sx={{
                                  fontSize: '0.875rem',
                                  color: '#424242',
                                  fontWeight: 400
                                }}
                              >
                                {String.fromCharCode(65 + optIndex)}. {option}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          mt: 1.5,
                          pt: 1.5,
                          borderTop: '1px solid #e8e8e8'
                        }}
                      ></Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Create Assessment Modal */}
      <ReactModal
        isOpen={modalOpen}
        onRequestClose={handleCloseModal}
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: '15px',
            border: 'none',
            boxShadow: '0 2px 8px 0 #00000029',
            padding: 0,
            maxWidth: '900px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000
          }
        }}
        contentLabel='Create Assessment'
      >
        <CreateAssessmentModal closeModal={handleCloseModal} template={templateForModal} />
      </ReactModal>
    </>
  )
}
